import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { MagnifyingGlass, Spinner, CircleNotch, Warning, CheckCircle } from "@phosphor-icons/react";
import HelpTip from "@/components/onboarding/HelpTip";

const sevColor = { critical: "var(--danger)", warning: "var(--warning)", info: "var(--text-2)" };

export default function SeoAudit() {
  const [url, setUrl] = useState("");
  const [pages, setPages] = useState(4);
  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = () => api.listAudits().then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setAudit(null);
    try {
      const r = await api.runAudit(url.trim(), Number(pages));
      setAudit(r.data);
      loadHistory();
      toast.success(`Audited ${r.data.pages_count} pages · score ${r.data.avg_score}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally {
      setLoading(false);
    }
  };

  const openAudit = async (id) => {
    setLoading(true);
    try {
      const r = await api.getAudit(id);
      setAudit(r.data);
    } finally { setLoading(false); }
  };

  return (
    <div className="fade-up space-y-6 max-w-6xl">
      <div className="border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
        <h2 className="text-base font-bold mb-1 flex items-center">Technical SEO Crawl<HelpTip text="Score is out of 100, based on things like page titles, headings, content length and structure. More pages gives a fuller picture but takes longer -- start with 4-8 pages, then go deeper later." /></h2>
        <p className="text-sm mb-4" style={{ color: "var(--text-2)" }}>Crawl and audit pages for on-page & technical SEO, then get AI fixes.</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input data-testid="audit-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
            placeholder="yourdomain.com" className="flex-1 mono text-sm px-4 py-3 border bg-[#0b1220] outline-none focus:border-[var(--accent)]" style={{ borderColor: "var(--line)" }} />
          <select data-testid="audit-pages-select" value={pages} onChange={(e) => setPages(e.target.value)} className="mono text-sm px-3 py-3 border bg-[#0b1220] outline-none" style={{ borderColor: "var(--line)" }}>
            {[2, 4, 6, 8, 12, 25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} pages</option>)}
          </select>
          <button data-testid="run-audit-button" onClick={run} disabled={loading}
            className="px-6 py-3 text-sm font-bold flex items-center gap-2 justify-center text-white disabled:opacity-60 hover:opacity-90 transition-opacity" style={{ background: "var(--primary)" }}>
            {loading ? <CircleNotch size={17} className="animate-spin" /> : <MagnifyingGlass size={17} weight="bold" />}
            {loading ? "Crawling…" : "Run Audit"}
          </button>
        </div>
      </div>

      {loading && !audit && (
        <div className="border p-10 text-center bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
          <CircleNotch size={30} className="animate-spin mx-auto mb-3" color="var(--accent)" />
          <p className="mono text-sm" style={{ color: "var(--text-2)" }}>Crawling pages & generating AI recommendations…</p>
        </div>
      )}

      {audit && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[["Avg Score", audit.avg_score, "var(--accent)"], ["Pages", audit.pages_count, "var(--primary)"],
              ["Critical", audit.critical_issues, "var(--danger)"], ["Total Issues", audit.total_issues, "var(--warning)"]].map(([l, v, c]) => (
              <div key={l} className="border p-4 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
                <div className="text-xs uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{l}</div>
                <div className="mono text-2xl font-bold mt-2" style={{ color: c }}>{v}</div>
              </div>
            ))}
          </div>

          <div className="border bg-[var(--surface)] overflow-x-auto" style={{ borderColor: "var(--line)" }}>
            <table className="w-full text-sm">
              <thead>
                <tr className="mono text-[11px] uppercase tracking-wider text-left" style={{ color: "var(--text-2)" }}>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>URL</th>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>Score</th>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>Words</th>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>Title</th>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>H1</th>
                  <th className="p-3 border-b" style={{ borderColor: "var(--line)" }}>Issues</th>
                </tr>
              </thead>
              <tbody>
                {audit.pages.map((p, i) => (
                  <tr key={p.url} data-testid={`audit-row-${i}`} className="hover:bg-[#0e1626] transition-colors align-top">
                    <td className="p-3 border-b mono text-xs max-w-[220px] truncate" style={{ borderColor: "var(--line)" }} title={p.url}>{p.url.replace(/^https?:\/\//, "")}</td>
                    <td className="p-3 border-b mono font-bold" style={{ borderColor: "var(--line)", color: p.score > 70 ? "var(--success)" : p.score > 50 ? "var(--warning)" : "var(--danger)" }}>{p.score}</td>
                    <td className="p-3 border-b mono" style={{ borderColor: "var(--line)" }}>{p.word_count}</td>
                    <td className="p-3 border-b" style={{ borderColor: "var(--line)" }}>{p.title_len ? <CheckCircle size={16} color="var(--success)" /> : <Warning size={16} color="var(--danger)" />}</td>
                    <td className="p-3 border-b mono" style={{ borderColor: "var(--line)" }}>{p.h1_count}</td>
                    <td className="p-3 border-b" style={{ borderColor: "var(--line)" }}>
                      <div className="flex flex-col gap-1">
                        {p.issues.length === 0 && <span className="mono text-xs" style={{ color: "var(--success)" }}>clean</span>}
                        {p.issues.map((iss, j) => (
                          <span key={`${i}-${j}`} className="mono text-[11px]" style={{ color: sevColor[iss.severity] }}>• {iss.message}</span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }} data-testid="ai-recommendations">
            <h2 className="text-base font-bold mb-4 flex items-center gap-2"><Spinner size={18} weight="duotone" color="var(--accent)" /> AI Recommendations</h2>
            <div className="prose-cs text-sm"><ReactMarkdown>{audit.recommendations}</ReactMarkdown></div>
          </div>
        </>
      )}

      {history.length > 0 && !audit && !loading && (
        <div className="border p-6 bg-[var(--surface)]" style={{ borderColor: "var(--line)" }}>
          <h2 className="text-base font-bold mb-4">History</h2>
          <div className="space-y-2">
            {history.map((a) => (
              <div key={a.id} data-testid="history-row" onClick={() => openAudit(a.id)} className="flex items-center justify-between border px-4 py-3 cursor-pointer hover:bg-[#0e1626] transition-colors" style={{ borderColor: "var(--line)" }}>
                <span className="mono text-sm truncate">{a.url}</span>
                <span className="mono text-xs" style={{ color: a.avg_score > 70 ? "var(--success)" : "var(--warning)" }}>score {a.avg_score} · {a.pages_count}p</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
